import { useCallback } from 'react';

/**
 * Minimal router contract required by this hook.
 *
 * We only depend on `replace()` because sidebar-close behavior performs an
 * in-place URL update (no new history entry needed for this interaction).
 */
interface RouterLike {
  /** Replaces the current route URL. */
  replace: (url: string) => void;
}

/**
 * Minimal search params contract required by this hook.
 *
 * Kept intentionally small to make the hook easy to test with lightweight mocks.
 */
interface SearchParamsLike {
  /** Iterator for current query entries, used to clone and mutate params safely. */
  entries: () => IterableIterator<[string, string]>;
  /** Checks if a query key exists before doing any URL manipulation work. */
  has: (key: string) => boolean;
}

/**
 * Input params for `useManagedUserScoreSidebarCloseHandler`.
 */
export interface UseManagedUserScoreSidebarCloseHandlerParams {
  /** Current route pathname (without query string). */
  pathname: string;
  /** Router instance used to apply URL replacement. */
  router: RouterLike;
  /** Current query params object from routing layer. */
  searchParams: SearchParamsLike;
}

export interface UseManagedUserScoreSidebarCloseHandlerResult {
  /**
   * Closes score sidebar by removing `sessionId` from query params.
   *
   * Behavior:
   * 1) No-op if `sessionId` does not exist
   * 2) Remove only `sessionId`
   * 3) Preserve all other query params
   * 4) Replace current URL with cleaned query string
   */
  handleScoreSidebarClose: () => void;
}

/**
 * Encapsulates URL cleanup logic for closing the scorecard sidebar.
 *
 * This hook isolates routing concerns from feature orchestration so the
 * behavior is independently testable and reusable.
 */
export const useManagedUserScoreSidebarCloseHandler = ({
  pathname,
  router,
  searchParams,
}: UseManagedUserScoreSidebarCloseHandlerParams): UseManagedUserScoreSidebarCloseHandlerResult => {
  /**
   * Memoized close handler consumed by sidebar UI.
   *
   * Memoization keeps function identity stable unless routing inputs change.
   */
  const handleScoreSidebarClose = useCallback(() => {
    // Only proceed when score sidebar deep-link parameter exists.
    if (searchParams.has('sessionId')) {
      // Clone current query params so we can mutate safely.
      const currentParams = new URLSearchParams(Array.from(searchParams.entries()));

      // Remove the score-sidebar session deep-link key.
      currentParams.delete('sessionId');

      // Rebuild URL while preserving any remaining params.
      const newQueryString = currentParams.toString();
      const newUrl = newQueryString ? `${pathname}/?${newQueryString}` : pathname;

      // Replace current URL to avoid adding an unnecessary history entry.
      router.replace(newUrl);
    }
  }, [pathname, router, searchParams]);

  // Stable hook API
  return {
    handleScoreSidebarClose,
  };
};
